"use client"

import { useState } from "react"
import { Button } from "#/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "#/components/ui/dialog"
import { Label } from "#/components/ui/label"
import { Textarea } from "#/components/ui/textarea"
import { LoadingSpinner } from "#/components/ui/loading-spinner"
import { useUpdateProfile } from "#/lib/api/react-query/useUpdateProfile"
import { useQueryClient } from "@tanstack/react-query"
import { getYourProfileQueryKey } from "#/lib/api/react-query/useGetYourProfile"
import { encryptWithSymmetricKey } from "#/lib/encryption/crypto"
import { useCiphers } from "#/hooks/use-ciphers"

interface EditProfileModalProps {
  isOpen: boolean
  onClose: () => void
  currentBio?: string
}

export function EditProfileModal({
  isOpen,
  onClose,
  currentBio = "",
}: EditProfileModalProps) {
  const [bio, setBio] = useState(currentBio)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const queryClient = useQueryClient()
  const { data: ciphers } = useCiphers()

  const updateProfileMutation = useUpdateProfile({
    mutation: {
      onSuccess: async () => {
        // Invalidate the profile query to refresh the data
        await queryClient.invalidateQueries({
          queryKey: getYourProfileQueryKey(),
        })
        onClose()
        setIsSubmitting(false)
      },
      onError: error => {
        console.error("Failed to update profile:", error)
        setIsSubmitting(false)
      },
    },
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!ciphers?.profileSymmetricKey) {
      console.error("Profile symmetric key not available")
      return
    }

    setIsSubmitting(true)

    try {
      // Encrypt the bio using the profile symmetric key
      const bioBuffer = new TextEncoder().encode(bio)
      const encryptedBio = await encryptWithSymmetricKey(
        bioBuffer,
        ciphers.profileSymmetricKey,
      )

      // Send the encrypted bio to the server
      updateProfileMutation.mutate({
        data: {
          bio: encryptedBio,
        },
      })
    } catch (error) {
      console.error("Failed to encrypt bio:", error)
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      setBio(currentBio) // Reset to original value
      onClose()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Profile</DialogTitle>
          <DialogDescription>
            Make changes to your profile here. Click save when you&apos;re done.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="bio">Bio</Label>
            <Textarea
              id="bio"
              placeholder="Tell us about yourself..."
              value={bio}
              onChange={e => setBio(e.target.value)}
              rows={4}
              disabled={isSubmitting}
              className="resize-none"
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <LoadingSpinner className="mr-2 h-4 w-4" />
                  Saving...
                </>
              ) : (
                "Save changes"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
